import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import {Divider, FormControl, MenuItem, Select} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import {useTranslation} from 'react-i18next';
import clsx from 'clsx';
import {useTheme} from '@mui/material/styles';
import React, {useEffect, useState} from 'react';
import _ from 'lodash';
import Chip from '@mui/material/Chip';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const useStyles = makeStyles((theme) => ({
    border: {
        borderRadius: 8,
        '& .MuiSelect-outlined': {
            padding: '6px 8px !important',
        },
        '&.MuiSelect-iconOpen': {
            paddingLeft: 5,
        },
        '&.smaller': {
            height: 32,
        },
        '&.small': {
            height: 36,
        },
        '&.medium': {
            height: 40,
        },
        '&.large': {
            height: 44,
        },

    },
    optionDisabled: {
        color: theme.palette.text.disabled,
    },
    renderValue: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0px 8px',
        gap: 10,
    },
    box: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 6,
        minWidth: 20,
        height: 20,
        '&.background': {
            backgroundColor: theme.palette.primary.main,
            color: '#ffffff',
        },
        '&.large': {
            width: 24,
            height: 24,
        },
    },
    label: {
        fontFamily: 'Inter',
        fontWeight: 400,
        fontSize: 12,
        color: theme.palette.mode === 'light' ? '#494B4C' : '#ffffff',
        '&.medium': {
            fontSize: 14,
        },
        '&.large': {
            fontSize: 16,
        },
    },
    menu: {
        marginTop: 10,
        padding: 0,
        backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : '#313233',
        boxShadow: theme.palette.mode === 'light' ? '0px 1px 8px 0px rgba(0, 0, 0, 0.15)' : 'unset',
        maxHeight: 400,
    },
    divider: {
        margin: 10,
    },
    menuItem: {
        padding: 0,
        fontSize: 12,
        paddingRight: 10,
    },
    menuItemNone: {
        paddingRight: 10,
    },
}));

const AgyleMultiselectDropdown = (props) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const theme = useTheme();

    const [selectAllChecked, setSelectAllChecked] = useState(false);
    const [selectNoneChecked, setSelectNoneChecked] = useState(true);
    const [valuesMap, setValuesMap] = useState({});
    const [values, setValues] = useState([]);
    const [options, setOptions] = useState([]);
    const [optionsMap, setOptionsMap] = useState([]);
    const [rememberedValue, setRememberedValue] = useState([]); // Remembered value before select all or select none is clicked

    // Sort options, if some object is selected then it should be on top of options
    useEffect(() => {
        const iteratees = [
            (o) => {
                if (o?.disabled) {
                    return 1;
                }
                return 0;
            },
            (o) => {
                return _.find(props.value, {[props.comparePath]: o?.[props.comparePath]});
            },
            props.optionLabel,
        ];
        const sortedOptions = _.orderBy(props.options, iteratees, ['asc']);
        setOptions(sortedOptions);
        const _optionsMap = _.reduce(props.options,
            (obj, param) => {
                obj[param[props.pathToItemValue] ? param[props.pathToItemValue] : param] = param;
                return obj;
            },
            {});
        setOptionsMap(_optionsMap);
        if (props.value && props.value.length > 0) {
            setValuesMap(_.reduce(props.value,
                (obj, param) => {
                    obj[param?.[props.pathToItemValue] ? param[props.pathToItemValue] : param] = 1;
                    return obj;
                },
                {}));
        } else {
            setValuesMap({});
        }
        if (props.value && props.value.length > 0) {
            const _values = _.filter(_.map(props.value, (v) => {
                if (props.pathToItemValue && v?.[props.pathToItemValue]) {
                    return v[props.pathToItemValue];
                } else {
                    return v;
                }
            }), v => {
                return !_optionsMap[v]?.disabled;
            });
            setValues(_values);
        } else {
            setValues([]);
        }
        //count only enabled values
    }, [props.options, props.value]);

    const handleChange = (e) => {
        const {value} = e.target;
        let clone = [...value];

        if (clone.includes('select_all')) {
            if (selectAllChecked) {
                setSelectAllChecked(false);
                setSelectNoneChecked(true);
                clone = props.rememberPreviousValue ? rememberedValue : [];
            } else {
                setSelectAllChecked(true);
                setSelectNoneChecked(false);
                if (props.pathToItemValue) {
                    clone = _.map(props.options, (o) => o[props.pathToItemValue]);
                } else {
                    clone = props.options;
                }
            }
            clone = clone.filter(v => (v !== 'select_all' && v !== 'select_none'));
        } else if (clone.includes('select_none')) {
            if (selectNoneChecked) {
                setSelectAllChecked(false);
                setSelectNoneChecked(false);
                clone = props.rememberPreviousValue ? rememberedValue : [];
            } else {
                setSelectAllChecked(false);
                setSelectNoneChecked(true);
                clone = [];
            }
        } else {
            if (props.rememberPreviousValue) {
                setRememberedValue(clone);
            }
            setSelectAllChecked(false);
            setSelectNoneChecked(false);
        }
        if (props.fullObjecValue && clone.length > 0) {
            clone = _.map(clone, o => {
                return optionsMap[o];
            });
        }
        props.onChange(clone, e);
    };

    const getFirstSelectedLabel = () => {
        if (values.length === 1) {
            return optionsMap[values[0]][props.optionLabel];
        }
        return '';
    };


    return (
        <FormControl fullWidth={props.fullWidth}>
            <Select
                value={values}
                onChange={handleChange}
                multiple
                name={props.name}
                disabled={props.disabled}
                displayEmpty
                className={clsx(classes.border, props.size)}
                MenuProps={{
                    MenuListProps: {disablePadding: true},
                    classes: {paper: clsx(classes.menu, 'scroll-normal-white')},
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                    },
                    transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                    },
                    sx: {
                        "&& .Mui-selected": {
                            backgroundColor: "unset"
                        }
                    }
                }}
                style={{
                    paddingRight: 16,//props.value?.length > 0 ? 16 : 0,
                    width: "100%",
                    ...props.style,
                }}
                IconComponent={ () => values.length < 2?
                    <div className={clsx(classes.box, classes.label, props.size)}>
                        <ArrowDropDownIcon/>
                    </div> :
                    <div className={clsx(classes.box, 'background', classes.label, props.size)}>
                        {values.length}
                    </div>
                }
                renderValue={(selected) => (
                   <div className={classes.renderValue} >
                     <>
                         {values.length === 1 &&
                            <span className={clsx(classes.label, props.size)}>
                                {getFirstSelectedLabel()}
                            </span>}
                         {values.length !== 1 &&
                             <span className={clsx(classes.label, props.size)}>
                                {props.label}
                            </span>}
                    </>
                    </div>
                )}
                sx={{
                    color: 'white',
                    '.MuiOutlinedInput-notchedOutline': {
                        borderColor: props.value.length === 0? (theme.palette.mode === 'light' ? '#acb1b5' : '#626466'): '#2180F7',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#2180F7',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.mode === 'light' ? '#626466' : '#ACAFB3',
                    },
                    '&.Mui-focused .MuiSvgIcon-root ': {
                        fill: '#2180F7 !important',
                    },
                }}
            >
                {(options?.length > 0 && props.showSelectAll) &&
                    <MenuItem value="select_all" className={classes.menuItem}>
                        <Checkbox
                            checked={props.value?.length === options.length}
                            indeterminate={props.value?.length > 0 && (props.value?.length !== options?.length)}
                            disableRipple
                        />
                        <div> {t('Select All')} </div>
                    </MenuItem>
                }

                {(options?.length > 0 && props.showSelectNone) &&
                    <MenuItem value="select_none" className={clsx(classes.menuItem, classes.menuItemNone)}>
                        <Checkbox
                            checked={props.value?.length === 0 && selectNoneChecked}
                            disableRipple
                        />
                        <div> {props.selectNoneLabel ? props.selectNoneLabel : t('Select None')} </div>
                    </MenuItem>
                }

                {(props.dividerAfterSelectAll && (props.showSelectAll || props.showSelectNone)) &&
                    <Divider className={classes.divider}/>
                }

                {options?.map((option) => (
                    <MenuItem key={option?.__id} value={(props.pathToItemValue) ? option[props.pathToItemValue] : option} className={classes.menuItem}>
                        <Checkbox
                            checked={valuesMap[props.pathToItemValue? option[props.pathToItemValue] : option]===1 && !option.disabled}
                            disableRipple
                            disabled={option.disabled}
                        />
                        <div className={option.disabled ? classes.optionDisabled : null}> {option[props.optionLabel]} {!props.optionActiveLabel || option.active || "(inactive)"}</div>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default AgyleMultiselectDropdown;

AgyleMultiselectDropdown.propTypes = {
    label: PropTypes.string,
    name: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.array.isRequired,
    disabled: PropTypes.bool,
    size: PropTypes.oneOf(['smaller', 'small', 'medium', 'large']),
    dividerAfterSelectAll: PropTypes.bool,
    showSelectAll: PropTypes.bool,
    showSelectNone: PropTypes.bool,
    rememberPreviousValue: PropTypes.bool,
    optionLabel: PropTypes.string,
    optionActiveLabel: PropTypes.bool,
    comparePath: PropTypes.string,
    pathToItemValue: PropTypes.string,
    fullObjecValue: PropTypes.bool,//if true and pathToItemValue is set onChange will return array of full objects not only array of 'ids'
    fullWidth: PropTypes.bool,
    autocomplete: PropTypes.bool,
};

AgyleMultiselectDropdown.defaultProps = {
    label: '',
    disabled: false,
    size: 'medium',
    showSelectAll: true,
    showSelectNone: true,
    rememberPreviousValue: true, // Memoize selected value before select all or select none is clicked
    dividerAfterSelectAll: true,
    optionLabel: '__description',
    comparePath: '__id',
    fullObjecValue: false,
    fullWidth: true,
    autocomplete: false,
};